import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { RoundModel, HoleStatsModel } from '@/models/RoundModels';
import { CourseModel } from '@/models/CourseModels';
import { Timestamp } from 'firebase/firestore';
import { getTeeColors } from '../utils/teeUtils';
import { calcNetScore } from '../utils/handicapUtils';
import { getScoreColors, getScoreRelativeToParColors, getScoreRelativeToParBorderRadius, getScoreRelativeToParBorder } from '../utils/themeUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import usePageTitle from '@/hooks/usePageTitle';
import { resolveType } from '@/services/ServiceContainer';
import { RoundService } from '@/services/RoundService';
import { CourseService } from '@/services/CourseService';
import { BackgroundSyncService } from '@/services/BackgroundSyncService';
import { ButtonSpinner } from '../components/LoadingSpinner';

const HoleEntry: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { theme } = useTheme();
    const { user } = useAuth();

    // Get roundId, courseId, and holeNumber from either URL params or location state
    const roundId = params.roundId || location.state?.roundId;
    const courseId = params.courseId || location.state?.courseId;
    const holeNumber = params.holeNumber ? parseInt(params.holeNumber) : location.state?.selectedHole || 1;

    const [currentHole, setCurrentHole] = useState<number>(holeNumber);
    const [holeStatsModel, setHoleStatsModel] = useState<Record<number, HoleStatsModel>>({});
    const [courseModel, setCourseModel] = useState<CourseModel | null>(null);
    const [roundModel, setRoundModel] = useState<RoundModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [holeInfo, setHoleInfo] = useState<{
        number: number;
        par: number;
        mensHandicap18: number | null;
        womensHandicap18: number | null;
        mensHandicap9: number | null;
        womensHandicap9: number | null;
        distance: number;
        teeName: string;
    } | null>(null);
    const [syncError, setSyncError] = useState<string | null>(null);
    const [isFinishing, setIsFinishing] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const STATS_EXPANDER_KEY = 'holeEntryStatsExpanderOpen';
    const GPS_EXPANDER_KEY = 'holeEntryGPSExpanderOpen';
    const [statsExpanded, setStatsExpanded] = useState(() => {
        const saved = localStorage.getItem(STATS_EXPANDER_KEY);
        return saved === null ? false : saved === 'true';
    });
    const [gpsExpanded, setGpsExpanded] = useState(() => {
        const saved = localStorage.getItem(GPS_EXPANDER_KEY);
        return saved === null ? false : saved === 'true';
    });

    // Calculate score relative to par
    const getScoreRelativeToPar = () => {
        if (!courseModel || !courseModel.scorecard || !courseModel.scorecard.holes) return null;

        let totalGrossScore = 0;
        let totalPar = 0;

        // Add scores for all holes that have been entered
        Object.entries(holeStatsModel).forEach(([hole, stats]) => {
            if (stats.grossScore !== null && stats.grossScore !== undefined) {
                const holeNumber = parseInt(hole);
                const holeInfo = courseModel.scorecard.holes[holeNumber - 1];
                if (holeInfo) {
                    totalPar += holeInfo.par;
                    totalGrossScore += stats.grossScore;
                }
            }
        });

        const difference = totalGrossScore - totalPar;
        if (difference === 0) return 'E';
        return difference > 0 ? `+${difference}` : difference.toString();
    };

    // Set page title
    usePageTitle(holeInfo ? `Hole ${holeInfo.number}` : 'Score');

    // Check sync status whenever holeStats changes
    useEffect(() => {
        const checkSyncStatus = async () => {
            if (!roundId) return;

            try {
                const roundService = resolveType(RoundService);
                const roundModel = await roundService.get(roundId);
                if (roundModel) {
                    setRoundModel(roundModel);
                    //setHoleStatsModel(roundModel.users[user!.uid].holeStats);
                }
                // if (roundModel) {
                //     const firestoreData = roundDoc.data() as Round;
                //     const localData = getLocalRoundData();
                //     if (localData && JSON.stringify(localData) !== JSON.stringify(firestoreData)) {
                //         setHasUnsyncedChanges(true);
                //     } else {
                //         setHasUnsyncedChanges(false);
                //     }
                // }
            } catch (error) {
                console.warn('Could not check sync status:', error);
            }
        };

        checkSyncStatus();
    }, [roundId, holeStatsModel]);

    // Load existing round data when component mounts
    useEffect(() => {
        const loadExistingRound = async () => {
            if (!roundId || !user) {
                setSyncError('No round ID or user found');
                setLoading(false);
                return;
            }

            try {
                // Try to load round data from local storage first

                const courseService = resolveType(CourseService);
                const roundService = resolveType(RoundService);

                const roundModel = await roundService.get(roundId);
                setRoundModel(roundModel);

                console.log('roundModel', roundModel);
                console.log('roundId', roundId);
                console.log('courseId', courseId);
                console.log('holeNumber', holeNumber);

                if (!roundId) {
                    throw new Error('Invalid round data: missing round ID');
                }

                if (!roundModel) {
                    throw new Error('Invalid round data: invalid round ID');
                }

                // Load course data from Firestore
                if (roundModel && (roundModel?.setup?.courseId || courseId)) {
                    const courseModel = await courseService.get(courseId);
                    if (courseModel) {
                        // Set course in state but don't store it in round data
                        setCourseModel(courseModel);
                        roundModel.setup.courseName = courseModel.name;
                    }
                } else {
                    throw new Error('Invalid round data: missing course ID');
                }

                // Set the current hole - use selectedHole from navigation state if available
                setCurrentHole(holeNumber);

                // Set hole stats
                const userData = roundModel.users[user.uid];
                if (userData) {
                    const userHoleStats: Record<number, HoleStatsModel> = {};
                    userData.holeStats.forEach(stat => {
                        userHoleStats[stat.hole] = {
                            hole: stat.hole,
                            par: stat.par,
                            grossScore: stat.grossScore,
                            grossScoreAdjusted: Math.min(stat.par + 2, stat.grossScore || 0),
                            netScore: stat.netScore,
                            fairwayHit: stat.fairwayHit,
                            greenInRegulation: stat.greenInRegulation,
                            putts: stat.putts,
                            chips: stat.chips,
                            penalties: stat.penalties
                        };
                    });
                    setHoleStatsModel(userHoleStats);
                }

                // Set played holes
                const otherUsersHoles = new Set<number>();
                const userHoles = new Set<number>();
                Object.values(roundModel.users).forEach(playerData => {
                    playerData.holeStats.forEach(stat => {
                        if (stat.grossScore !== null) {
                            if (playerData.uid === user.uid) {
                                userHoles.add(stat.hole);
                            } else {
                                otherUsersHoles.add(stat.hole);
                            }
                        }
                    });
                });
                // setOtherUsersPlayedHoles(otherUsersHoles);
                // setUserPlayedHoles(userHoles);

                // Save round data to local storage (without course data)
                await roundService.update(roundModel.id, user.uid, roundModel, null);
                setRoundModel(roundModel);

                setSyncError(null);
            } catch (error) {
                console.error('Error loading round data:', error);
                setSyncError(error instanceof Error ? error.message : 'Failed to load round data');
            } finally {
                setLoading(false);
            }
        };

        loadExistingRound();
    }, [roundId, user, holeNumber]);

    useEffect(() => {
        const loadTeeInfo = async () => {
            if (courseModel) {
                const currentHoleNumber = currentHole;
                const currentHoleInfo = courseModel.scorecard.holes[currentHoleNumber - 1];
                if (currentHoleInfo) {
                    // Get the selected tee from local round data or Firestore round data
                    let selectedTeeName = '';
                    if (roundModel && roundModel.setup && roundModel.setup.selectedTee) {
                        selectedTeeName = roundModel.setup.selectedTee;
                    } else if (roundModel && roundModel.users && user && roundModel.users[user.uid] && roundModel.users[user.uid].selectedTee) {
                        selectedTeeName = roundModel.users[user.uid].selectedTee;
                    } else if (courseModel.tees && courseModel.tees.length > 0) {
                        selectedTeeName = courseModel.tees[0].teeName;
                    }
                    const selectedTee = currentHoleInfo.tees.find(t => t.name === selectedTeeName);
                    if (selectedTee) {
                        setHoleInfo({
                            number: currentHoleNumber,
                            par: currentHoleInfo.par,
                            mensHandicap18: currentHoleInfo.mensHandicap18,
                            womensHandicap18: currentHoleInfo.womensHandicap18,
                            mensHandicap9: currentHoleInfo.mensHandicap9,
                            womensHandicap9: currentHoleInfo.womensHandicap9,
                            distance: selectedTee.yards,
                            teeName: selectedTeeName
                        });
                    } else {
                        setSyncError('Could not find selected tee information');
                    }
                } else {
                    setSyncError('Could not find hole information');
                }
            }
        };

        loadTeeInfo();
    }, [currentHole, courseModel, user]);

    // const handleManualSync = async () => {
    //     setSyncStatus('syncing');
    //     setSyncError(null);
    //     try {
    //         const success = await forceSync();
    //         if (success) {
    //             setSyncStatus('success');
    //             setHasUnsyncedChanges(false);
    //         } else {
    //             setSyncStatus('error');
    //             setHasUnsyncedChanges(true);
    //             setSyncError('Failed to sync data. Please try again.');
    //         }
    //     } catch (error) {
    //         setSyncStatus('error');
    //         setHasUnsyncedChanges(true);
    //         setSyncError(error instanceof Error ? error.message : 'Failed to sync data');
    //     }
    // };


    const saveCurrentHole = async () => {
        if (!roundId || !user || !courseModel || !roundModel) return;

        // Create a clean copy of the hole stats, removing any undefined values
        const cleanHoleStats = Object.entries(holeStatsModel)
            .filter(([_, stats]) => stats !== null && stats !== undefined)
            .reduce((acc, [hole, stats]) => {
                // Ensure all properties are either their value or null, never undefined
                const cleanStats = {
                    hole: parseInt(hole),
                    par: stats.par || 0, // par should never be null
                    grossScore: stats.grossScore !== undefined ? stats.grossScore : null,
                    grossScoreAdjusted: stats.grossScoreAdjusted !== undefined ? stats.grossScoreAdjusted : null,
                    netScore: stats.netScore !== undefined ? stats.netScore : null,
                    fairwayHit: stats.fairwayHit !== undefined ? stats.fairwayHit : null,
                    greenInRegulation: stats.greenInRegulation !== undefined ? stats.greenInRegulation : null,
                    putts: stats.putts !== undefined ? stats.putts : null,
                    chips: stats.chips !== undefined ? stats.chips : null,
                    penalties: stats.penalties !== undefined ? stats.penalties : null
                };
                acc[hole] = cleanStats;
                return acc;
            }, {} as Record<string, HoleStatsModel>);

        // Create updated round data with clean hole stats
        const updatedRoundModel: RoundModel = {
            ...roundModel,
            setup: {
                ...roundModel.setup,
                startingHole: currentHole
            },
            users: {
                ...roundModel.users,
                [user.uid]: {
                    ...roundModel.users[user.uid],
                    holeStats: Object.values(cleanHoleStats)
                }
            },
            updatedAt: Timestamp.now()
        };

        const roundService = resolveType(RoundService);
        await roundService.update(roundId, user.uid, updatedRoundModel, courseModel);
        setRoundModel(updatedRoundModel);
    };

    const handlePrevHole = async () => {
        if (!roundId || !user || !courseModel) return;

        try {
            setIsNavigating(true);
            // Save current hole data if there are changes
            await saveCurrentHole();

            // Check if we're on hole 10 and playing only back 9
            const isBack9Only = roundModel!.setup.holesToPlay === 'back9';
            const isHole10 = currentHole === 10;

            // Don't allow going to previous hole if we're on hole 10 and playing only back 9
            if (isBack9Only && isHole10) {
                return;
            }

            if (currentHole > 1) {
                const prevHole = currentHole - 1;
                setCurrentHole(prevHole);
                navigate(`/hole-entry/${courseId}/${roundId}/${prevHole}`, { state: { ...location.state, selectedHole: prevHole } });
            }
        } finally {
            setIsNavigating(false);
        }
    };

    const handleNextHole = async () => {
        if (!roundId || !user || !courseModel) return;

        try {
            setIsNavigating(true);
            // Save current hole data
            await saveCurrentHole();

            // Get the maximum hole number based on round setup
            const maxHole = roundModel!.setup.holesToPlay === 'front9' ? 9 :
                roundModel!.setup.holesToPlay === 'back9' ? 18 :
                    roundModel!.setup.holesToPlay === 'all18' ? 18 : 18;

            // If this is the last hole, navigate to summary
            if (currentHole === maxHole) {
                try {
                    setIsFinishing(true);
                    // Queue a sync but don't wait for it
                    const backgroundSync = resolveType(BackgroundSyncService);
                    backgroundSync.queueSync(roundModel!.id);
                    navigate(`/round-scorecard/${courseId}/${roundModel!.id}`);
                } catch (error) {
                    console.error('Error navigating to summary:', error);
                    setSyncError('Failed to load round data');
                    setIsFinishing(false);
                }
            } else if (currentHole < maxHole) {
                // Only move to next hole if we haven't reached the maximum
                const nextHole = currentHole + 1;
                // Queue a sync but don't wait for it
                const backgroundSync = resolveType(BackgroundSyncService);
                backgroundSync.queueSync(roundModel!.id);
                setCurrentHole(nextHole);
                navigate(`/hole-entry/${courseId}/${roundId}/${nextHole}`, { state: { ...location.state, selectedHole: nextHole } });
            }
        } finally {
            setIsNavigating(false);
        }
    };

    // Add global navigation interceptor
    useEffect(() => {
        const handleBeforeNavigate = async (e?: BeforeUnloadEvent) => {
            await saveCurrentHole();
            const roundService = resolveType(RoundService);
            const roundModel = await roundService.get(roundId);
            if (roundModel) {
                const backgroundSync = resolveType(BackgroundSyncService);
                backgroundSync.queueSync(roundModel.id);
            }
            setRoundModel(roundModel);
            if (e) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        // Intercept all navigation events
        window.addEventListener('popstate', handleBeforeNavigate);
        window.addEventListener('beforeunload', handleBeforeNavigate);

        // Cleanup
        return () => {
            window.removeEventListener('popstate', handleBeforeNavigate);
            window.removeEventListener('beforeunload', handleBeforeNavigate);
        };
    }, [currentHole, holeStatsModel]);

    // Add navigation handler for navbar links
    useEffect(() => {
        const handleNavClick = async (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('a[href="/"]') || target.closest('a[href="/profile"]')) {
                e.preventDefault();
                await saveCurrentHole();
                if (roundModel) {
                    const backgroundSync = resolveType(BackgroundSyncService);
                    backgroundSync.queueSync(roundModel.id);
                }
                setRoundModel(roundModel);
                navigate(target.closest('a')?.getAttribute('href') || '/');
            }
        };

        document.addEventListener('click', handleNavClick);
        return () => document.removeEventListener('click', handleNavClick);
    }, [currentHole, holeStatsModel]);

    // Add navigation handler for the scorecard button
    const handleScorecardClick = async () => {
        try {
            setIsNavigating(true);
            await saveCurrentHole();
            if (roundModel) {
                const backgroundSync = resolveType(BackgroundSyncService);
                backgroundSync.queueSync(roundModel.id);
            }
            setRoundModel(roundModel);
            navigate(`/round-scorecard/${courseId}/${roundModel?.id}`);
        } finally {
            setIsNavigating(false);
        }
    };

    // Add navigation handler for the next hole button
    const handleNextHoleClick = async () => {
        await handleNextHole();
    };

    // Add navigation handler for the previous hole button
    const handlePrevHoleClick = async () => {
        await handlePrevHole();
    };

    const toggleStatsExpander = () => {
        setStatsExpanded((prev) => {
            localStorage.setItem(STATS_EXPANDER_KEY, String(!prev));
            return !prev;
        });
    };

    const toggleGPSExpander = () => {
        setGpsExpanded((prev) => {
            localStorage.setItem(GPS_EXPANDER_KEY, String(!prev));
            return !prev;
        });
    };

    // Add cleanup effect for unmounting
    useEffect(() => {
        const saveAndSync = async () => {
            // Save current hole and queue sync when component unmounts
            try {
                if (!roundId || !user || !courseModel || !roundModel) {
                    console.log('Skipping save on unmount - missing required data');
                    return;
                }

                // Create a clean copy of the hole stats, removing any undefined values
                const cleanHoleStats = Object.entries(holeStatsModel)
                    .filter(([_, stats]) => stats !== null && stats !== undefined)
                    .reduce((acc, [hole, stats]) => {
                        // Ensure all properties are either their value or null, never undefined
                        const cleanStats = {
                            hole: parseInt(hole),
                            par: stats.par || 0, // par should never be null
                            grossScore: stats.grossScore !== undefined ? stats.grossScore : null,
                            grossScoreAdjusted: stats.grossScoreAdjusted !== undefined ? stats.grossScoreAdjusted : null,
                            netScore: stats.netScore !== undefined ? stats.netScore : null,
                            fairwayHit: stats.fairwayHit !== undefined ? stats.fairwayHit : null,
                            greenInRegulation: stats.greenInRegulation !== undefined ? stats.greenInRegulation : null,
                            putts: stats.putts !== undefined ? stats.putts : null,
                            chips: stats.chips !== undefined ? stats.chips : null,
                            penalties: stats.penalties !== undefined ? stats.penalties : null
                        };
                        acc[hole] = cleanStats;
                        return acc;
                    }, {} as Record<string, HoleStatsModel>);

                // Create updated round data with clean hole stats
                const updatedRoundModel: RoundModel = {
                    ...roundModel,
                    setup: {
                        ...roundModel.setup,
                        startingHole: currentHole
                    },
                    users: {
                        ...roundModel.users,
                        [user.uid]: {
                            ...roundModel.users[user.uid],
                            holeStats: Object.values(cleanHoleStats)
                        }
                    },
                    updatedAt: Timestamp.now()
                };

                const roundService = resolveType(RoundService);
                await roundService.update(roundId, user.uid, updatedRoundModel, courseModel);
                setRoundModel(updatedRoundModel);

                if (roundModel) {
                    const backgroundSync = resolveType(BackgroundSyncService);
                    backgroundSync.queueSync(roundModel.id);
                }
            } catch (error) {
                console.error('Error saving hole on unmount:', error);
            }
        };
        saveAndSync();
    }, [currentHole, holeStatsModel]);

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: theme.colors.BACKGROUND,
                color: theme.colors.TEXT
            }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (syncError) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: theme.colors.BACKGROUND,
                color: theme.colors.ERROR,
                padding: theme.spacing.MEDIUM,
                textAlign: 'center',
                gap: theme.spacing.MEDIUM
            }}>
                <div>{syncError}</div>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                        padding: theme.spacing.SMALL,
                        backgroundColor: theme.colors.PRIMARY,
                        color: theme.colors.BACKGROUND,
                        border: 'none',
                        borderRadius: theme.borderRadius.MEDIUM,
                        cursor: 'pointer'
                    }}
                >
                    Return to Dashboard
                </button>
            </div>
        );
    }

    //console.log('holeInfo', holeInfo);

    if (!courseModel || !holeInfo) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: theme.colors.BACKGROUND,
                color: theme.colors.ERROR,
                padding: theme.spacing.MEDIUM,
                textAlign: 'center',
                gap: theme.spacing.MEDIUM
            }}>
                <div>Error: Could not load round data</div>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                        padding: theme.spacing.SMALL,
                        backgroundColor: theme.colors.PRIMARY,
                        color: theme.colors.BACKGROUND,
                        border: 'none',
                        borderRadius: theme.borderRadius.MEDIUM,
                        cursor: 'pointer'
                    }}
                >
                    Return to Dashboard
                </button>
            </div>
        );
    }

    const teeColors = getTeeColors(holeInfo.teeName, { courseModel: courseModel, currentHole: holeInfo.number, currentYards: holeInfo.distance });
    // console.log('teeColors', teeColors);

    return (
        <div style={{
            padding: theme.spacing.MEDIUM,
            paddingBottom: '120px'
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: theme.spacing.SMALL
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing.SMALL
                }}>
                    <h1 style={{
                        color: theme.colors.TEXT,
                        minWidth: '100px',
                        textAlign: 'left'
                    }}>
                        Hole {holeInfo.number}
                    </h1>
                    <span style={{
                        color: theme.colors.TEXT,
                        fontSize: '2em',
                        fontWeight: 'bold',
                        minWidth: '100px',
                        textAlign: 'right'
                    }}>
                        {holeInfo.distance} yds
                    </span>
                </div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: theme.colors.TEXT_SECONDARY,
                    fontSize: '1.1em',
                    alignItems: 'center',
                    width: '100%'
                }}>
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.SMALL
                    }}>
                        <div className="text-sm font-medium text-gray-700">Tee:</div>
                        <div
                            className="px-2 py-1 rounded text-sm font-medium"
                            style={{
                                backgroundColor: teeColors.bg,
                                color: teeColors.text,
                                padding: '8px 12px',
                                borderRadius: '4px',
                                borderColor: (teeColors.bg.toLowerCase() === '#ffffff' ? '#efefef' : teeColors.bg),
                                borderWidth: '1px',
                                borderStyle: 'solid'
                            }}
                        >
                            {teeColors.content}
                        </div>
                    </div>

                    <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '60px',
                            height: '60px',
                            borderRadius: getScoreRelativeToParBorderRadius(getScoreRelativeToPar()),
                            backgroundColor: getScoreRelativeToParColors(theme, getScoreRelativeToPar()).bg,
                            color: getScoreRelativeToParColors(theme, getScoreRelativeToPar()).text,
                            border: getScoreRelativeToParBorder(theme, getScoreRelativeToPar()),
                            fontWeight: 'bold',
                            fontSize: '1.4em'
                        }}>
                            {getScoreRelativeToPar()}
                        </div>
                    </div>

                    <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center'
                    }}>
                        <span style={{
                            fontSize: '1.2em',
                            fontWeight: 'bold'
                        }}>
                            Hcp: {(() => {
                                // Get the round data from local storage
                                if (roundModel && roundModel.setup.holesToPlay === 'all18') {
                                    return holeInfo.mensHandicap18;
                                } else {
                                    return holeInfo.mensHandicap9;
                                }
                            })()}
                        </span>
                    </div>
                </div>
            </div>

            <div style={{
                backgroundColor: theme.colors.BACKGROUND,
                padding: theme.spacing.MEDIUM,
                borderRadius: theme.borderRadius.MEDIUM,
                marginBottom: theme.spacing.LARGE
            }}>
                <div style={{ display: 'grid', gap: theme.spacing.MEDIUM }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)',
                        gap: theme.spacing.SMALL,
                        maxWidth: '400px'
                    }}>
                        {Array.from({ length: 9 }, (_, i) => i + 1).map((score) => {
                            const isOverPar = score > holeInfo.par;
                            const isUnderPar = score < holeInfo.par;
                            const isPar = score === holeInfo.par;

                            return (
                                <button
                                    key={score}
                                    onClick={() => {
                                        if (!user) return;
                                        setHoleStatsModel(prev => {
                                            const newScore = prev[currentHole]?.grossScore === score ? null : score;
                                            const handicap = roundModel?.users[user.uid]?.handicap || 0;
                                            const holesToPlay = roundModel?.setup.holesToPlay || 'all18';

                                            console.log('currentHole', currentHole);
                                            console.log('course.scorecard.holes[holeIndex]', courseModel.scorecard.holes[currentHole - 1]);

                                            const netScore = calcNetScore({
                                                holesToPlay,
                                                grossScore: newScore,
                                                holeInfo: courseModel.scorecard.holes[currentHole - 1],
                                                handicap
                                            });

                                            return {
                                                ...prev,
                                                [currentHole]: {
                                                    ...prev[currentHole],
                                                    grossScore: newScore,
                                                    netScore: netScore
                                                }
                                            };
                                        });
                                    }}
                                    style={{
                                        aspectRatio: '1',
                                        padding: 0,
                                        backgroundColor: holeStatsModel[currentHole]?.grossScore === score
                                            ? getScoreColors(theme, score, holeInfo.par).bg
                                            : theme.colors.BACKGROUND,
                                        color: holeStatsModel[currentHole]?.grossScore === score ? getScoreColors(theme, score, holeInfo.par).text : theme.colors.TEXT,
                                        border: `1px solid ${theme.colors.BORDER}`,
                                        borderRadius: isUnderPar ? '50%' : isOverPar ? '4px' : '0',
                                        cursor: 'pointer',
                                        fontSize: '1.2em',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    {score}
                                    {isPar && (
                                        <span style={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            backgroundColor: theme.colors.PAR,
                                            color: theme.colors.PAR_TEXT,
                                            fontSize: '0.7em',
                                            padding: '2px 4px',
                                            borderRadius: '4px'
                                        }}>
                                            PAR
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div style={{ marginTop: theme.spacing.LARGE }}>
                <button
                    type="button"
                    onClick={toggleStatsExpander}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'none',
                        border: 'none',
                        color: theme.colors.PRIMARY,
                        fontWeight: 600,
                        fontSize: '1.1em',
                        cursor: 'pointer',
                        marginBottom: theme.spacing.SMALL,
                        gap: 8,
                    }}
                >
                    <span
                        style={{
                            display: 'inline-block',
                            transform: statsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                        }}
                    >
                        ▶
                    </span>
                    Stats
                </button>
                {statsExpanded && (
                    <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.MEDIUM }}>
                            <div>
                                <label style={{ color: theme.colors.TEXT, display: 'block', marginBottom: theme.spacing.SMALL, fontSize: '1.2em', fontWeight: 'bold' }}>
                                    Fairway Hit
                                </label>
                                {holeInfo.par === 3 ? (
                                    <div style={{
                                        display: 'flex', gap: theme.spacing.SMALL,
                                        backgroundColor: theme.colors.BACKGROUND,
                                        color: theme.colors.TEXT_SECONDARY,
                                        textAlign: 'center',
                                        fontSize: '1.2em',
                                        maxWidth: '200px'
                                    }}>
                                        N/A
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: theme.spacing.SMALL, maxWidth: '200px' }}>
                                        {[true, false].map((value) => (
                                            <button
                                                key={String(value)}
                                                onClick={() => setHoleStatsModel(prev => ({
                                                    ...prev,
                                                    [currentHole]: {
                                                        ...prev[currentHole],
                                                        fairwayHit: prev[currentHole]?.fairwayHit === value ? null : value
                                                    }
                                                }))}
                                                style={{
                                                    flex: 1,
                                                    padding: theme.spacing.SMALL,
                                                    backgroundColor: holeStatsModel[currentHole]?.fairwayHit === value
                                                        ? (value ? theme.colors.SUCCESS : theme.colors.BLACK)
                                                        : theme.colors.BACKGROUND,
                                                    color: holeStatsModel[currentHole]?.fairwayHit === value ? theme.colors.WHITE : theme.colors.TEXT,
                                                    border: `1px solid ${theme.colors.BORDER}`,
                                                    borderRadius: value ? '0' : '4px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {value ? 'Yes' : 'No'}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label style={{ color: theme.colors.TEXT, display: 'block', marginBottom: theme.spacing.SMALL, fontSize: '1.2em', fontWeight: 'bold' }}>
                                    Green in Reg
                                </label>
                                <div style={{ display: 'flex', gap: theme.spacing.SMALL, maxWidth: '200px' }}>
                                    {[true, false].map((value) => (
                                        <button
                                            key={String(value)}
                                            onClick={() => setHoleStatsModel(prev => ({
                                                ...prev,
                                                [currentHole]: {
                                                    ...prev[currentHole],
                                                    greenInRegulation: prev[currentHole]?.greenInRegulation === value ? null : value
                                                }
                                            }))}
                                            style={{
                                                flex: 1,
                                                padding: theme.spacing.SMALL,
                                                backgroundColor: holeStatsModel[currentHole]?.greenInRegulation === value
                                                    ? (value ? theme.colors.SUCCESS : theme.colors.BLACK)
                                                    : theme.colors.BACKGROUND,
                                                color: holeStatsModel[currentHole]?.greenInRegulation === value ? theme.colors.WHITE : theme.colors.TEXT,
                                                border: `1px solid ${theme.colors.BORDER}`,
                                                borderRadius: value ? '0' : '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {value ? 'Yes' : 'No'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.MEDIUM }}>
                            <div>
                                <label style={{ color: theme.colors.TEXT, display: 'block', marginBottom: theme.spacing.SMALL, fontSize: '1.2em', fontWeight: 'bold' }}>
                                    Chips
                                </label>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: theme.spacing.SMALL,
                                    maxWidth: '200px'
                                }}>
                                    {Array.from({ length: 5 }, (_, i) => i).map((chips) => {
                                        const isPar = chips === 0;

                                        return (
                                            <button
                                                key={chips}
                                                onClick={() => setHoleStatsModel(prev => ({
                                                    ...prev,
                                                    [currentHole]: {
                                                        ...prev[currentHole],
                                                        chips: prev[currentHole]?.chips === chips ? null : chips
                                                    }
                                                }))}
                                                style={{
                                                    aspectRatio: '1',
                                                    padding: 0,
                                                    backgroundColor: holeStatsModel[currentHole]?.chips === chips
                                                        ? (isPar ? theme.colors.SUCCESS : theme.colors.OVER_PAR)
                                                        : theme.colors.BACKGROUND,
                                                    color: holeStatsModel[currentHole]?.chips === chips ? theme.colors.WHITE : theme.colors.TEXT,
                                                    border: `1px solid ${theme.colors.BORDER}`,
                                                    borderRadius: isPar ? '0' : '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2em',
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {chips}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label style={{ color: theme.colors.TEXT, display: 'block', marginBottom: theme.spacing.SMALL, fontSize: '1.2em', fontWeight: 'bold' }}>
                                    Putts
                                </label>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: theme.spacing.SMALL,
                                    maxWidth: '200px'
                                }}>
                                    {Array.from({ length: 5 }, (_, i) => i).map((putts) => {
                                        const isOverPar = putts >= 3;
                                        const isPar = putts === 2;

                                        return (
                                            <button
                                                key={putts}
                                                onClick={() => setHoleStatsModel(prev => ({
                                                    ...prev,
                                                    [currentHole]: {
                                                        ...prev[currentHole],
                                                        putts: prev[currentHole]?.putts === putts ? null : putts
                                                    }
                                                }))}
                                                style={{
                                                    aspectRatio: '1',
                                                    padding: 0,
                                                    backgroundColor: holeStatsModel[currentHole]?.putts === putts
                                                        ? (isPar ? theme.colors.SUCCESS : isOverPar ? theme.colors.OVER_PAR : theme.colors.ERROR)
                                                        : theme.colors.BACKGROUND,
                                                    color: holeStatsModel[currentHole]?.putts === putts ? theme.colors.WHITE : theme.colors.TEXT,
                                                    border: `1px solid ${theme.colors.BORDER}`,
                                                    borderRadius: isPar ? '0' : isOverPar ? '4px' : '50%',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2em',
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {putts}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.MEDIUM }}>
                            <div>
                                <label style={{ color: theme.colors.TEXT, display: 'block', marginBottom: theme.spacing.SMALL, fontSize: '1.2em', fontWeight: 'bold' }}>
                                    Penalties
                                </label>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: theme.spacing.SMALL,
                                    maxWidth: '200px'
                                }}>
                                    {Array.from({ length: 4 }, (_, i) => i).map((penalties) => {
                                        const isPar = penalties === 0;

                                        return (
                                            <button
                                                key={penalties}
                                                onClick={() => setHoleStatsModel(prev => ({
                                                    ...prev,
                                                    [currentHole]: {
                                                        ...prev[currentHole],
                                                        penalties: prev[currentHole]?.penalties === penalties ? null : penalties
                                                    }
                                                }))}
                                                style={{
                                                    aspectRatio: '1',
                                                    padding: 0,
                                                    backgroundColor: holeStatsModel[currentHole]?.penalties === penalties
                                                        ? (isPar ? theme.colors.SUCCESS : theme.colors.OVER_PAR)
                                                        : theme.colors.BACKGROUND,
                                                    color: holeStatsModel[currentHole]?.penalties === penalties ? theme.colors.WHITE : theme.colors.TEXT,
                                                    border: `1px solid ${theme.colors.BORDER}`,
                                                    borderRadius: isPar ? '0' : '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2em',
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {penalties}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div style={{ marginTop: theme.spacing.LARGE }}>
                <button
                    type="button"
                    onClick={toggleGPSExpander}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'none',
                        border: 'none',
                        color: theme.colors.PRIMARY,
                        fontWeight: 600,
                        fontSize: '1.1em',
                        cursor: 'pointer',
                        marginBottom: theme.spacing.SMALL,
                        gap: 8,
                    }}
                >
                    <span
                        style={{
                            display: 'inline-block',
                            transform: gpsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                        }}
                    >
                        ▶
                    </span>
                    GPS
                </button>
                {gpsExpanded && (
                    <div>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing.SMALL,
                            maxWidth: '200px'
                        }}>
                            {['Back', 'Center', 'Front'].map((position) => (
                                <div key={position} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: theme.spacing.SMALL,
                                    backgroundColor: theme.colors.BACKGROUND,
                                    border: `1px solid ${theme.colors.BORDER}`,
                                    borderRadius: theme.borderRadius.SMALL
                                }}>
                                    <span style={{ color: theme.colors.TEXT }}>{position}</span>
                                    <span style={{ color: theme.colors.TEXT_SECONDARY }}>Coming soon</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* {otherUsersPlayedHoles.has(currentHole) && (
                <div style={{
                    backgroundColor: theme.colors.WARNING,
                    color: theme.colors.BACKGROUND,
                    padding: theme.spacing.MEDIUM,
                    borderRadius: theme.borderRadius.MEDIUM,
                    marginBottom: theme.spacing.MEDIUM,
                    textAlign: 'center'
                }}>
                    This hole has already been played by another user
                </div>
            )} */}

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: theme.spacing.MEDIUM,
                gap: theme.spacing.MEDIUM
            }}>
                <button
                    onClick={handleScorecardClick}
                    style={{
                        position: 'fixed',
                        bottom: theme.spacing.LARGE,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        minWidth: '80px',
                        height: '56px',
                        borderRadius: '28px',
                        backgroundColor: theme.colors.BUTTON_PRIMARY_BG,
                        color: theme.colors.BUTTON_PRIMARY_TEXT,
                        border: 'none',
                        cursor: isNavigating ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                        zIndex: 1000,
                        opacity: isNavigating ? 0.7 : 1,
                        fontSize: '1rem',
                        fontWeight: '600'
                    }}
                    title="View Scorecard"
                    disabled={isNavigating}
                >
                    {isNavigating ? (
                        <ButtonSpinner size={24} color={theme.colors.BACKGROUND} />
                    ) : (
                        'Card'
                    )}
                </button>

                <button
                    onClick={handleNextHoleClick}
                    disabled={currentHole > 18 || isFinishing || isNavigating}
                    style={{
                        position: 'fixed',
                        bottom: theme.spacing.LARGE,
                        right: theme.spacing.LARGE,
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: currentHole === 18 ? theme.colors.BUTTON_PRIMARY_BG : theme.colors.BUTTON_SECONDARY_BG,
                        color: theme.colors.BUTTON_SECONDARY_TEXT,
                        border: 'none',
                        cursor: (currentHole > 18 || isFinishing || isNavigating) ? 'not-allowed' : 'pointer',
                        opacity: (currentHole > 18 || isFinishing || isNavigating) ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                        zIndex: 1000
                    }}
                    title={currentHole === 18 ? 'Review' : 'Next'}
                >
                    {isFinishing || isNavigating ? (
                        <ButtonSpinner size={24} color={theme.colors.BACKGROUND} />
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 4L20 12L12 20L10.59 18.59L16.17 13H4V11H16.17L10.59 5.41L12 4Z" fill="currentColor" />
                        </svg>
                    )}
                </button>

                <button
                    onClick={handlePrevHoleClick}
                    disabled={currentHole <= 1 || (roundModel?.setup.holesToPlay === 'back9' && currentHole === 10) || isNavigating}
                    style={{
                        position: 'fixed',
                        bottom: theme.spacing.LARGE,
                        left: theme.spacing.LARGE,
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: theme.colors.BUTTON_SECONDARY_BG,
                        color: theme.colors.BUTTON_SECONDARY_TEXT,
                        border: 'none',
                        cursor: (currentHole <= 1 || (roundModel?.setup.holesToPlay === 'back9' && currentHole === 10) || isNavigating) ? 'not-allowed' : 'pointer',
                        opacity: (currentHole <= 1 || (roundModel?.setup.holesToPlay === 'back9' && currentHole === 10) || isNavigating) ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                        zIndex: 1000
                    }}
                    title="Previous"
                >
                    {isNavigating ? (
                        <ButtonSpinner size={24} color={theme.colors.BACKGROUND} />
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 20L4 12L12 4L13.41 5.41L7.83 11H20V13H7.83L13.41 18.59L12 20Z" fill="currentColor" />
                        </svg>
                    )}
                </button>
            </div>
        </div>
    );
};

export default HoleEntry; 